import {
  BATTLE_ACTIONS_PER_TURN,
  BATTLE_DECK_SIZE,
  BATTLE_MAX_HAND,
  BATTLE_STARTING_HAND,
  BATTLE_MINI_TUTORIAL,
  BATTLE_PRESTIGE_TO_WIN,
  BATTLE_TUTORIALS,
  battleTutorialById,
  cpuName,
  createTutorialBattle,
  isCpuDifficultyUnlocked,
  isTutorialComplete,
  isTutorialDone,
  isTutorialLocked,
  markTutorialComplete,
  pickCpuInput,
  PILE_LABELS,
  pileUnlockRound,
  reduceBattle,
  resetCpuDifficultyWins,
  resetTutorialProgress,
  tutorialAllows,
  tutorialCursor,
  tutorialEndRecap,
  tutorialFocusStat,
  tutorialInput,
  tutorialLastCpuStep,
  tutorialStepCount,
  tutorialStepMoves,
  unlockAllCpuDifficulties,
  type BattleState,
  type BattleTutorial,
  type PileIndex,
  type PlayerId,
  type TutorialStep,
} from 'features/battle/index';
import {
  BadgesBattleView,
  BattleBadge,
  BattleOpenBadgeProvider,
  useBattleOpenBadge,
} from 'features/battle/views/BadgesBattleView';
import { Badge } from 'features/catalog/useBadges';
import React, { useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTalosData } from 'shared/context/TalosDataContext';
import { BATTLE_BADGES_PATH, BATTLE_HOW_TO_PATH, battleScenarioPath } from 'shared/lib/nav-paths';
import './BadgesBattleView.css';
import './BattleHowToView.css';

/** Scales the full 9rem badge down to its chip; see .bb-how-mini-grow. */
const MINI_GROW_STYLE = { '--bb-mini-scale': 'calc(100cqw / 9rem)' } as React.CSSProperties;

const DECK_RARITIES = [
  'common',
  'common',
  'rare',
  'common',
  'rare',
  'legendary',
  'common',
  'rare',
  'mythic',
  'common',
  'rare',
  'legendary',
] as const;

/** SRT Squire (common) and In Debt! (rare). Mini in the deck; hover scales up to the full badge. */
const DECK_EXAMPLE_SLOTS: Record<number, string> = {
  0: 'gcr2',
  2: 'pq4t',
};

const STEPS = [
  {
    kicker: 'Step 1',
    title: `Build a deck of ${BATTLE_DECK_SIZE} badges`,
    body: 'Each badge has stats and a single effect you can activate. Culture, Engineering, Growth, Fun, or Spotlight',
  },
  {
    kicker: 'Step 2',
    title: `${BATTLE_ACTIONS_PER_TURN} actions each turn`,
    body: (
      <>
        On your turn, take {BATTLE_ACTIONS_PER_TURN} actions: <strong className="bb-how-action">Draw</strong>,{' '}
        <strong className="bb-how-action">Reveal</strong>, or <strong className="bb-how-action">Activate</strong>{' '}
        badges.
      </>
    ),
  },
  {
    kicker: 'Step 3',
    title: 'Reveal onto a pile',
    body: 'The board has four piles. Piles unlock one turn at a time.',
  },
  {
    kicker: 'Step 4',
    title: 'Have the Stat Lead on an Effect to Activate It',
    body: 'You can only activate a badge of a pile if your cumulative stat for that effect is higher than your opponent’s. You can activate the same effect multiple times.',
  },
  {
    kicker: 'Step 5',
    title: `First to ${BATTLE_PRESTIGE_TO_WIN} Prestige wins!`,
    body: (
      <>
        Use effects to score <strong className="bb-how-action">Prestige</strong>. Reach {BATTLE_PRESTIGE_TO_WIN}{' '}
        <strong className="bb-how-action">Prestige</strong> first and you win. If you go first and get there, your
        opponent still gets one last turn.
      </>
    ),
  },
] as const;

const DeckFigure: React.FC = () => {
  const { badgesMap } = useTalosData();
  const onOpenBadge = useBattleOpenBadge();

  return (
    <div className="bb-how-figure">
      <div className="bb-how-deck">
        {DECK_RARITIES.map((rarity, index) => {
          const example = DECK_EXAMPLE_SLOTS[index] ? badgesMap.get(DECK_EXAMPLE_SLOTS[index]) : undefined;
          if (!example) return <span key={index} className={`bb-how-mini rarity-${rarity}`} aria-hidden />;
          return (
            <span key={example.id} className={`bb-how-mini is-example rarity-${rarity}`} tabIndex={0}>
              <span className="bb-how-mini-grow" style={MINI_GROW_STYLE}>
                <BattleBadge badge={example} onClick={onOpenBadge ? () => onOpenBadge(example) : undefined} />
                <span className="bb-how-mini-name" aria-hidden>
                  {example.name}
                </span>
              </span>
            </span>
          );
        })}
      </div>
      <p className="bb-how-caption">{BATTLE_DECK_SIZE} badges you've claimed, or a sample deck</p>
    </div>
  );
};

const TurnFigure: React.FC = () => (
  <div className="bb-how-figure" aria-hidden>
    <div className="bb-how-turn">
      <div className="bb-how-turn-count">
        <strong>3</strong>
        <span>Turn</span>
      </div>
      <div className="bb-how-actions">
        <span>Draw</span>
        <span>Reveal</span>
      </div>
      <div className="bb-how-hand">
        {Array.from({ length: BATTLE_STARTING_HAND }, (_, index) => (
          <span key={index} className="bb-how-card" />
        ))}
      </div>
    </div>
    <p className="bb-how-caption">
      {BATTLE_ACTIONS_PER_TURN} actions · hand of {BATTLE_STARTING_HAND}, up to {BATTLE_MAX_HAND}
    </p>
  </div>
);

const PilesFigure: React.FC = () => (
  <div className="bb-how-figure" aria-hidden>
    <div className="bb-how-piles">
      {PILE_LABELS.map((label, index) => {
        const pile = index as PileIndex;
        const unlock = pileUnlockRound(pile);
        const open = unlock <= 2;
        return (
          <div key={label} className={`bb-how-pile rarity-${label.toLowerCase()}${open ? '' : ' is-locked'}`}>
            <span className="bb-how-pile-name">{label}</span>
            {open ? <span className="bb-how-card" /> : <span className="bb-how-lock">Turn {unlock}</span>}
          </div>
        );
      })}
      <div className="bb-how-pile is-discard">
        <span className="bb-how-pile-name">Discard</span>
        <span className="bb-how-card is-spent" />
      </div>
    </div>
    <p className="bb-how-caption">Some piles are limited to which rarities can be placed on them.</p>
  </div>
);

const LeadFigure: React.FC = () => (
  <div className="bb-how-figure" aria-hidden>
    <div className="bb-how-lead">
      <div className="bb-how-stat">
        <span className="bb-how-who">You</span>
        <span className="bb-how-stat-rows">
          <span className="bb-how-stat-row">
            <span>Engineering</span>
            <span className="bb-how-bar">
              <span className="is-lead" style={{ width: '80%' }} />
            </span>
            <b>8</b>
          </span>
          <span className="bb-how-stat-row is-aside">
            <span>Fun</span>
            <span className="bb-how-bar">
              <span style={{ width: '40%' }} />
            </span>
            <b>4</b>
          </span>
          <span className="bb-how-stat-row is-aside">
            <span>Spotlight</span>
            <span className="bb-how-bar">
              <span style={{ width: '20%' }} />
            </span>
            <b>2</b>
          </span>
        </span>
      </div>
      <div className="bb-how-stat is-behind">
        <span className="bb-how-who">Opponent</span>
        <span className="bb-how-stat-rows">
          <span className="bb-how-stat-row">
            <span>Engineering</span>
            <span className="bb-how-bar">
              <span style={{ width: '30%' }} />
            </span>
            <b>3</b>
          </span>
          <span className="bb-how-stat-row is-aside">
            <span>Fun</span>
            <span className="bb-how-bar">
              <span style={{ width: '40%' }} />
            </span>
            <b>4</b>
          </span>
          <span className="bb-how-stat-row is-aside">
            <span>Spotlight</span>
            <span className="bb-how-bar">
              <span className="is-lead" style={{ width: '70%' }} />
            </span>
            <b>7</b>
          </span>
        </span>
      </div>
      <div className="bb-how-played">
        <span className="bb-how-card" />
        <em>Activated</em>
      </div>
    </div>
  </div>
);

const WinFigure: React.FC = () => (
  <div className="bb-how-figure" aria-hidden>
    <div className="bb-how-score">
      <div className="is-win">
        <span>You</span>
        <strong>{BATTLE_PRESTIGE_TO_WIN}</strong>
      </div>
      <div>
        <span>Opponent</span>
        <strong>6</strong>
      </div>
    </div>
    <p className="bb-how-caption">First to {BATTLE_PRESTIGE_TO_WIN}. Going first? They still get a last turn.</p>
  </div>
);

const FIGURES = [DeckFigure, TurnFigure, PilesFigure, LeadFigure, WinFigure];

const DEFAULT_CPU_INTRO = 'It’s the CPU’s turn. Click Next to watch their move.';

type CoachScreen = {
  title: string;
  number: number;
  text: string;
  /** Marked read when the player clicks Next or Finish. Null when the player should act on the board instead. */
  marks: string[] | null;
  /** The CPU has been told to move and the player is watching. */
  waiting: boolean;
  /** Last recap after the battle ends. The button says Finish instead of Next. */
  finish?: boolean;
  /** Board stat the current coach text is talking about. */
  stat?: ReturnType<typeof tutorialFocusStat>;
};

type CoachScreenSpec = {
  number: number;
  steps: number;
  read: ReadonlySet<string>;
};

const buildTutorialScreens = (tutorial: BattleTutorial) => {
  const screens: CoachScreenSpec[] = [];
  const preScreenNumber = new Map<string, number>();
  const mainScreenNumber = new Map<string, number>();
  const read = new Set<string>();
  let applied = 0;

  for (const turn of tutorial.turns) {
    for (let index = 0; index < turn.steps.length; index += 1) {
      const step = turn.steps[index];
      const moveCount = tutorialStepMoves(step).length;
      const key = `${turn.round}-${turn.who}-${index}`;
      const hasPre = Boolean(step.intro) || (turn.who === 1 && index === 0);
      if (hasPre) {
        const number = screens.length + 1;
        preScreenNumber.set(key, number);
        screens.push({ number, steps: applied, read: new Set(read) });
        if (turn.who === 0 && step.intro) read.add(`intro:${key}`);
      }
      if (turn.who === 0) {
        const number = screens.length + 1;
        mainScreenNumber.set(key, number);
        screens.push({ number, steps: applied, read: new Set(read) });
        applied += moveCount;
      } else {
        applied += moveCount;
        const number = screens.length + 1;
        mainScreenNumber.set(key, number);
        screens.push({ number, steps: applied, read: new Set(read) });
        read.add(`recap:${key}`);
      }
    }
  }

  if (tutorial.freePlay) {
    screens.push({ number: screens.length + 1, steps: applied, read: new Set(read) });
  }

  return { screens, preScreenNumber, mainScreenNumber };
};

const TUTORIAL_SCREENS = new Map(BATTLE_TUTORIALS.map((tutorial) => [tutorial.id, buildTutorialScreens(tutorial)]));

const screensFor = (tutorial: BattleTutorial) => TUTORIAL_SCREENS.get(tutorial.id) ?? buildTutorialScreens(tutorial);

const screenWithStat = (screen: Omit<CoachScreen, 'stat'>, step: TutorialStep | null | undefined): CoachScreen => ({
  ...screen,
  stat: step ? tutorialFocusStat(step, screen.text) : null,
});

const moveMark = (key: string) => `move:${key}`;

const freePlayTurn = (tutorial: BattleTutorial): { round: number; who: PlayerId } | null => {
  if (!tutorial.freePlay) return null;
  const lastTurn = tutorial.turns[tutorial.turns.length - 1];
  if (!lastTurn) return null;
  return {
    round: lastTurn.who === 1 ? lastTurn.round + 1 : lastTurn.round,
    who: lastTurn.who === 1 ? 0 : 1,
  };
};

const isInFreePlayTurn = (tutorial: BattleTutorial, state: BattleState): boolean => {
  const target = freePlayTurn(tutorial);
  if (!target || state.phase === 'game_over') return false;
  return state.round === target.round && state.activePlayer === target.who;
};

const isAtFreePlayStart = (tutorial: BattleTutorial, state: BattleState): boolean =>
  isInFreePlayTurn(tutorial, state) &&
  state.phase === 'turn' &&
  !state.pending &&
  state.players[state.activePlayer].actionsTaken === 0;

/** What the coach shows: a recap of the CPU's last move, a prompt before its next one, an intro, or your step. */
const coachScreen = (tutorial: BattleTutorial, state: BattleState, read: ReadonlySet<string>): CoachScreen | null => {
  const { screens, preScreenNumber, mainScreenNumber } = screensFor(tutorial);
  const endRecap = tutorialEndRecap(tutorial, state);
  if (endRecap && !read.has(`recap:${endRecap.key}`)) {
    return screenWithStat(
      {
        title: endRecap.turn.title,
        number: mainScreenNumber.get(endRecap.key) ?? screens.length,
        text: endRecap.step.say,
        marks: [`recap:${endRecap.key}`],
        waiting: false,
        finish: true,
      },
      endRecap.step
    );
  }
  const cursor = tutorialCursor(tutorial, state);
  if (!cursor) {
    if (state.phase === 'game_over') return null;
    const lastCpu = tutorialLastCpuStep(tutorial);
    if (lastCpu && !read.has(`recap:${lastCpu.key}`)) {
      return screenWithStat(
        {
          title: lastCpu.turn.title,
          number: mainScreenNumber.get(lastCpu.key) ?? screens.length,
          text: lastCpu.step.say,
          marks: [`recap:${lastCpu.key}`],
          waiting: false,
        },
        lastCpu.step
      );
    }
    if (tutorial.freePlay && isAtFreePlayStart(tutorial, state)) {
      return {
        title: tutorial.freePlay.title,
        number: screens.length,
        text: tutorial.freePlay.say,
        marks: null,
        waiting: false,
        stat: null,
      };
    }
    return null;
  }
  const cpuTurn = cursor.turn.who === state.cpu;
  const recap = cursor.previous?.turn.who === state.cpu ? cursor.previous : null;
  const waitingForNextCpuMove = cpuTurn && !cursor.step.intro && read.has(moveMark(cursor.key));
  if (recap && (!read.has(`recap:${recap.key}`) || waitingForNextCpuMove)) {
    const marks = [`recap:${recap.key}`];
    if (cpuTurn && !cursor.step.intro) marks.push(moveMark(cursor.key));
    return screenWithStat(
      {
        title: recap.turn.title,
        number: mainScreenNumber.get(recap.key) ?? cursor.number,
        text: recap.step.say,
        marks,
        waiting: waitingForNextCpuMove,
      },
      recap.step
    );
  }
  if (cpuTurn) {
    const text = cursor.step.intro ?? DEFAULT_CPU_INTRO;
    return screenWithStat(
      {
        title: cursor.turn.title,
        number: preScreenNumber.get(cursor.key) ?? cursor.number,
        text,
        marks: [moveMark(cursor.key)],
        waiting: read.has(moveMark(cursor.key)),
      },
      cursor.step.intro ? cursor.step : null
    );
  }
  if (cursor.step.intro && !read.has(`intro:${cursor.key}`)) {
    return screenWithStat(
      {
        title: cursor.turn.title,
        number: preScreenNumber.get(cursor.key) ?? cursor.number,
        text: cursor.step.intro,
        marks: [`intro:${cursor.key}`],
        waiting: false,
      },
      cursor.step
    );
  }
  return screenWithStat(
    {
      title: cursor.turn.title,
      number: mainScreenNumber.get(cursor.key) ?? cursor.number,
      text: cursor.step.say,
      marks: null,
      waiting: false,
    },
    cursor.step
  );
};

const appliedSteps = (tutorial: BattleTutorial, state: BattleState): number => {
  if (state.phase === 'game_over' && state.winner === 0 && !tutorial.freePlay) return tutorialStepCount(tutorial);
  if (isAtFreePlayStart(tutorial, state)) return tutorialStepCount(tutorial);
  const cursor = tutorialCursor(tutorial, state);
  return cursor ? cursor.number - 1 : -1;
};

type UndoTarget = { steps: number; read: ReadonlySet<string> };

/** One coach screen back, or null on the first welcome screen. */
const undoTarget = (tutorial: BattleTutorial, state: BattleState, read: ReadonlySet<string>): UndoTarget | null => {
  if (appliedSteps(tutorial, state) < 0) return null;
  const screen = coachScreen(tutorial, state, read);
  if (!screen) return null;
  const { screens } = screensFor(tutorial);
  const current = screens[screen.number - 1];
  if (screen.waiting && current) {
    return { steps: current.steps, read: current.read };
  }
  const previous = screens[screen.number - 2];
  if (!previous) return null;
  return { steps: previous.steps, read: previous.read };
};

const tutorialCpuDifficulty = (tutorial: BattleTutorial) => tutorial.cpuDifficulty ?? 'easy';

const replayTutorial = (tutorial: BattleTutorial, playerName: string, seed: number, steps: number): BattleState => {
  let state = createTutorialBattle(tutorial, [playerName, cpuName(tutorialCpuDifficulty(tutorial))], seed);
  for (let i = 0; i < steps; i += 1) {
    const cursor = tutorialCursor(tutorial, state);
    if (!cursor) break;
    state = reduceBattle(state, tutorialInput(state, cursor.move));
  }
  return state;
};

const TutorialCoach: React.FC<{
  tutorial: BattleTutorial;
  state: BattleState;
  read: ReadonlySet<string>;
  onRead: (marks: string[]) => void;
  onUndo: () => void;
}> = ({ tutorial, state, read, onRead, onUndo }) => {
  const screen = coachScreen(tutorial, state, read);
  if (!screen) {
    if (state.phase !== 'game_over' || !tutorial.outro) return null;
    return (
      <>
        <div className="bb-coach-head">
          <p className="bb-coach-kicker">{state.winner === 0 ? 'You win!' : 'Battle over'}</p>
        </div>
        <p>{state.winner === 0 ? tutorial.outro : 'The battle went off script. Restart to try again.'}</p>
      </>
    );
  }
  const { marks } = screen;
  return (
    <>
      <div className="bb-coach-head">
        <p className="bb-coach-kicker">{screen.title}</p>
        <p className="bb-coach-progress">
          Step {screen.number} of {screensFor(tutorial).screens.length}
        </p>
      </div>
      <p>{screen.text}</p>
      <div className="bb-coach-actions">
        {marks ? (
          <button type="button" disabled={screen.waiting} onClick={() => onRead(marks)}>
            {screen.waiting ? 'CPU is playing…' : screen.finish ? 'Finish' : 'Next'}
          </button>
        ) : null}
        <button type="button" className="is-ghost" disabled={!undoTarget(tutorial, state, read)} onClick={onUndo}>
          Undo
        </button>
      </div>
    </>
  );
};

const TutorialMatch: React.FC<{
  tutorial: BattleTutorial;
  playerName: string;
  playerId: string;
  ownedBadgeIds: string[];
  onSelectBadge?: (badge: Badge) => void;
}> = ({ tutorial, playerName, playerId, ownedBadgeIds, onSelectBadge }) => {
  const navigate = useNavigate();
  const [run, setRun] = useState(0);
  /** Recaps and intros the player clicked past, and CPU moves they let happen. */
  const [read, setRead] = useState<ReadonlySet<string>>(() => new Set());
  const seedRef = useRef<number | null>(null);
  const replayRef = useRef(0);
  const liveRef = useRef<BattleState | null>(null);
  const cpuDifficulty = tutorialCpuDifficulty(tutorial);
  const markRead = (marks: string[]) => setRead((current) => new Set([...current, ...marks]));
  const restart = () => {
    seedRef.current = null;
    replayRef.current = 0;
    setRead(new Set());
    setRun((n) => n + 1);
  };
  const undo = () => {
    const state = liveRef.current;
    if (!state) return;
    const target = undoTarget(tutorial, state, read);
    if (!target) return;
    if (target.steps === appliedSteps(tutorial, state)) {
      setRead(new Set(target.read));
      return;
    }
    replayRef.current = target.steps;
    setRead(new Set(target.read));
    setRun((n) => n + 1);
  };
  return (
    <BadgesBattleView
      key={run}
      playerName={playerName}
      playerId={playerId}
      ownedBadgeIds={ownedBadgeIds}
      onSelectBadge={onSelectBadge}
      preset={{
        create: (seed) => {
          if (seedRef.current == null) seedRef.current = seed;
          return replayTutorial(tutorial, playerName, seedRef.current, replayRef.current);
        },
        difficulty: cpuDifficulty,
        label: `How to Play · ${tutorial.title}`,
        coach: (state) => {
          liveRef.current = state;
          if (!coachScreen(tutorial, state, read) && (state.phase !== 'game_over' || !tutorial.outro)) return null;
          return <TutorialCoach tutorial={tutorial} state={state} read={read} onRead={markRead} onUndo={undo} />;
        },
        allowInput: (state, input) =>
          input.type === 'pass' ||
          (!coachScreen(tutorial, state, read)?.marks && tutorialAllows(tutorial, state, input)),
        coaching: (state) => !coachScreen(tutorial, state, read)?.marks && Boolean(tutorialCursor(tutorial, state)),
        coachStat: (state) => coachScreen(tutorial, state, read)?.stat ?? null,
        coachOverview: (state) => coachScreen(tutorial, state, read)?.number === 1,
        cpuInput: (state) => {
          const cursor = tutorialCursor(tutorial, state);
          if (!cursor) return pickCpuInput(state, cpuDifficulty);
          return read.has(moveMark(cursor.key)) ? tutorialInput(state, cursor.move) : null;
        },
        cpuPauseMs: (state, defaultMs) => {
          if (tutorialCursor(tutorial, state) && state.players[state.activePlayer].actionsTaken === 0 && !state.pending)
            return 0;
          return Math.round(defaultMs / 2);
        },
        holdResult: (state) => Boolean(coachScreen(tutorial, state, read)?.finish),
        onGameOver: (state) => {
          if (state.winner === 0) markTutorialComplete(tutorial.id);
        },
        resultActions: (state) => {
          if (state.winner !== 0) {
            return (
              <button type="button" onClick={restart}>
                Try again
              </button>
            );
          }
          return (
            <>
              {tutorial.id === BATTLE_MINI_TUTORIAL.id ? (
                <p className="bb-modal-sub">Battle Badges and Arena unlocked</p>
              ) : (
                <p className="bb-modal-sub">Hard CPU unlocked in Battle and Arena</p>
              )}
              <button type="button" onClick={() => navigate(BATTLE_BADGES_PATH)}>
                Play Battle Badges
              </button>
              <button type="button" className="is-ghost" onClick={() => navigate(BATTLE_HOW_TO_PATH)}>
                Back to How to Play
              </button>
            </>
          );
        },
        onExit: () => navigate(BATTLE_HOW_TO_PATH),
      }}
    />
  );
};

const cpuFullyUnlocked = (): boolean =>
  isCpuDifficultyUnlocked('impossible') && isCpuDifficultyUnlocked('impossible', 'arena');

const TutorialHome: React.FC = () => {
  const [complete, setComplete] = useState(isTutorialComplete);
  const [doneIds, setDoneIds] = useState(() => BATTLE_TUTORIALS.map((tutorial) => tutorial.id).filter(isTutorialDone));
  const [full, setFull] = useState(cpuFullyUnlocked);
  const [rulesOpen, setRulesOpen] = useState(false);
  const refresh = () => {
    setComplete(isTutorialComplete());
    setDoneIds(BATTLE_TUTORIALS.map((tutorial) => tutorial.id).filter(isTutorialDone));
    setFull(cpuFullyUnlocked());
  };
  const devLabel = !complete ? 'Dev: unlock' : full ? 'Dev: lock' : 'Dev: unlock Impossible';
  return (
    <article className="bb-how">
      <header className="bb-how-intro">
        <p className="bb-how-eyebrow">Battle Badges</p>
        <h1>How to play</h1>
        <p>Complete Two Piles below to unlock Battle Badges, Battle Arena, and A Spotlight Comeback.</p>
      </header>
      <details className="bb-how-rules" open={rulesOpen}>
        <summary
          onClick={(event) => {
            event.preventDefault();
            setRulesOpen((open) => !open);
          }}
        >
          Rules
        </summary>
        {STEPS.map((step, index) => {
          const Figure = FIGURES[index];
          return (
            <section key={step.kicker} className="bb-how-step">
              <div className="bb-how-copy">
                <p className="bb-how-kicker">{step.kicker}</p>
                <h2>{step.title}</h2>
                <p>{step.body}</p>
              </div>
              <Figure />
            </section>
          );
        })}
      </details>
      <div className="bb-how-lessons">
        {BATTLE_TUTORIALS.map((tutorial) => {
          const done = doneIds.includes(tutorial.id);
          const locked = isTutorialLocked(tutorial);
          const steps = screensFor(tutorial).screens.length;
          const className = `bb-how-tutorial${done ? ' is-done' : ''}${tutorial.optional ? ' is-optional' : ''}${
            locked ? ' is-locked' : ''
          }`;
          const body = (
            <>
              <p className="bb-how-kicker">
                {locked ? tutorial.lockedKicker : done ? tutorial.doneKicker : `${tutorial.kicker} · ${steps} steps`}
              </p>
              <h2>{tutorial.title}</h2>
              <p>{tutorial.lesson}</p>
              <span>{locked ? tutorial.lockedPlay : done ? tutorial.replay : tutorial.play}</span>
            </>
          );
          if (locked) {
            return (
              <div key={tutorial.id} className={className}>
                {body}
              </div>
            );
          }
          return (
            <Link key={tutorial.id} to={battleScenarioPath(tutorial.id)} className={className}>
              {body}
            </Link>
          );
        })}
      </div>
      {process.env.NODE_ENV === 'development' ? (
        <button
          type="button"
          className="bb-how-dev"
          onClick={() => {
            if (!complete) markTutorialComplete(BATTLE_MINI_TUTORIAL.id);
            else if (!full) unlockAllCpuDifficulties();
            else {
              resetTutorialProgress();
              resetCpuDifficultyWins();
            }
            refresh();
          }}
        >
          {devLabel}
        </button>
      ) : null}
    </article>
  );
};

type HowToProps = {
  playerName?: string;
  playerId?: string;
  ownedBadgeIds?: string[];
  profileId?: string | null;
  onSelectBadge?: (badge: Badge) => void;
};

export const BattleHowToView: React.FC<HowToProps> = ({
  playerName = 'You',
  playerId = '',
  ownedBadgeIds = [],
  onSelectBadge,
}) => {
  const location = useLocation();
  const tutorialId = location.pathname.split('/').filter(Boolean)[2];
  if (!tutorialId) {
    return (
      <BattleOpenBadgeProvider onOpenBadge={onSelectBadge}>
        <TutorialHome />
      </BattleOpenBadgeProvider>
    );
  }
  const tutorial = battleTutorialById(tutorialId);
  if (!tutorial || isTutorialLocked(tutorial)) return <Navigate to={BATTLE_HOW_TO_PATH} replace />;
  return (
    <TutorialMatch
      tutorial={tutorial}
      playerName={playerName}
      playerId={playerId}
      ownedBadgeIds={ownedBadgeIds}
      onSelectBadge={onSelectBadge}
    />
  );
};
